import { useEffect, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import * as ButtonGroup from '@givebutter/primitives/button-group';
import { Label } from '@givebutter/primitives/label';
import { maskWholeNumber } from '@givebutter/util';
import {
    faCircle,
    faCopy,
    faEdit,
    faEnvelope as faEnvelopeOutline,
    faEye,
    faPlus,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope, faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EngageMessages, fetchEngageMessages } from 'api/engage';
import Content from 'assets/js/components/Content';
import * as DeleteLetterDialog from 'assets/js/pages/engage/letters/DeleteLetterAlertDialog';
import * as LetterEasySendNewLetterDialog from 'assets/js/pages/engage/letters/LetterEasySendNewLetterDialog';
import { LetterStatusBadge } from 'assets/js/pages/engage/letters/LetterStatusBadge';
import * as MarkLetterDialog from 'assets/js/pages/engage/letters/MarkLetterDialog';
import { useLetterActions } from 'assets/js/pages/engage/letters/useLetterActions';
import { StatusFilter } from 'assets/js/pages/engage/types';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as SubNav from 'components/sub-nav';
import { EngageSubNavTabs } from 'components/sub-nav/EngageSubNavTabs';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { EngageMessage, EngageMessageChannel, EngageMessageStatus } from 'types/domain';

const columnHelper = createColumnHelper<EngageMessage>();

const LettersIndex = () => {
    const queryClient = useQueryClient();
    const match = useRouteMatch();
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);
    const {
        letterIndexTab,
        setLetterIndexTab,
        hasDismissedEngageLetterEasySendCallout,
        setHasDismissedEngageLetterEasySendCallout,
    } = useLocalStorage(state => state);
    const { pagination, setPagination } = Table.useReactTableState({
        sortingState: [{ id: 'created_at', desc: true }],
    });

    const orderedTabParams =
        letterIndexTab === 'sent'
            ? { ordered: 1, notStatus: ['draft'] as EngageMessageStatus[] }
            : {};
    const printableTabParams =
        letterIndexTab === 'sending'
            ? { printable: 1, notStatus: ['draft'] as EngageMessageStatus[] }
            : {};
    const draftTabParams =
        letterIndexTab === 'draft' ? { status: ['draft'] as EngageMessageStatus[] } : {};

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
            query: debouncedSearch,
            sortByDesc: 'created_at',
            channel: ['letter'] as EngageMessageChannel[],
            ...orderedTabParams,
            ...printableTabParams,
            ...draftTabParams,
        },
        500,
    );

    const { data: letters } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessagesWithFilters(accountId, params),
        queryFn: () => fetchEngageMessages(accountId, params),
        select: data => ({
            ...data,
            counts: computeLetterCounts(data.counts),
        }),
    });

    const allLettersCount = letters?.counts?.all ?? 0;
    const { duplicateMessageMutation, invalidate } = useLetterActions();
    const { mutate: duplicateMessage } = duplicateMessageMutation;

    const columns = useMemo(
        () => [
            columnHelper.accessor('internal_name', {
                header: 'Name',
                cell: info => {
                    const message = info.row.original;

                    return (
                        <div className={'flex items-center gap-2 font-medium text-black'}>
                            {message.internal_name}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('recipient_count', {
                header: 'Recipients',
                cell: info => <p>{maskWholeNumber(info.getValue())}</p>,
            }),
            columnHelper.accessor('created_at', {
                header: 'Date created',
                cell: info => (
                    <p>{DateTime.fromISO(info.getValue()).toLocaleString(DateTime.DATE_MED)}</p>
                ),
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: info => {
                    const letter = info.row.original;

                    if (letter.status !== 'draft' && letter.sending_at && !letter.sent_at) {
                        return (
                            <Badge size="sm" color="warning">
                                Scheduled
                            </Badge>
                        );
                    }

                    const status = letter.status;
                    return <LetterStatusBadge status={status} />;
                },
            }),
            ...(letterIndexTab !== 'draft'
                ? []
                : [
                      columnHelper.display({
                          id: 'messageType',
                          header: 'Mailing type',
                          cell: info => {
                              const message = info.row.original;
                              const isFullService = message.settings.send_to_print_and_mail_service;
                              if (!isFullService) {
                                  return null;
                              }
                              return (
                                  <div
                                      className={
                                          'flex items-center gap-2 font-medium text-violet-500'
                                      }
                                  >
                                      <FontAwesomeIcon icon={faEnvelopeOutline} />
                                      Automated mailing
                                  </div>
                              );
                          },
                      }),
                  ]),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const message = info.row.original;
                    const isDraft = message.status === 'draft';
                    const isReadyToPrint = message.status === 'sending';
                    const isSent = message.status === 'sent';
                    const isEasySend = message.settings.send_to_print_and_mail_service;

                    return (
                        <DropdownMenu.Root key={message.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content
                                onSelect={e => e.preventDefault()}
                                sideOffset={5}
                                align={'end'}
                            >
                                {!isDraft && (
                                    <>
                                        <DropdownMenu.Item
                                            onClick={() =>
                                                history.push(`${match.url}/${message.id}`)
                                            }
                                            className={'flex items-center gap-2'}
                                        >
                                            <FontAwesomeIcon icon={faEye} />
                                            <p>View</p>
                                        </DropdownMenu.Item>

                                        <DropdownMenu.Item
                                            onClick={() =>
                                                history.push(
                                                    `${match.url}/details/${message.id}/edit`,
                                                )
                                            }
                                            className={'flex items-center gap-2'}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                            <p>Edit name</p>
                                        </DropdownMenu.Item>
                                    </>
                                )}
                                {!isEasySend && isReadyToPrint && (
                                    <MarkLetterDialog.Root isSending={true} messageId={message.id}>
                                        <MarkLetterDialog.Trigger type={'reset'} asChild={true}>
                                            <DropdownMenu.Item
                                                className={'flex items-center gap-2'}
                                                onSelect={e => e.preventDefault()}
                                            >
                                                <FontAwesomeIcon icon={faCircle} />
                                                <p>Mark letter sent</p>
                                            </DropdownMenu.Item>
                                        </MarkLetterDialog.Trigger>
                                    </MarkLetterDialog.Root>
                                )}
                                {!isEasySend && isSent && (
                                    <MarkLetterDialog.Root isSending={false} messageId={message.id}>
                                        <MarkLetterDialog.Trigger type={'reset'} asChild={true}>
                                            <DropdownMenu.Item
                                                className={'flex items-center gap-2'}
                                                onSelect={e => e.preventDefault()}
                                            >
                                                <FontAwesomeIcon icon={faRotateLeft} />
                                                <p>Unmark as sent</p>
                                            </DropdownMenu.Item>
                                        </MarkLetterDialog.Trigger>
                                    </MarkLetterDialog.Root>
                                )}
                                {isDraft && (
                                    <DropdownMenu.Item
                                        onClick={() =>
                                            history.push(
                                                `${match.url}/draft/${message.id}/edit/details`,
                                            )
                                        }
                                        className={'flex items-center gap-2'}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                        <p>Edit</p>
                                    </DropdownMenu.Item>
                                )}
                                <DropdownMenu.Item
                                    onClick={() => duplicateMessage(message.id)}
                                    className={'flex items-center gap-2'}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                    <p>Duplicate</p>
                                </DropdownMenu.Item>
                                <DeleteLetterDialog.Root
                                    messageId={message.id}
                                    onSuccess={() => {
                                        invalidate({
                                            type: 'all',
                                        });
                                    }}
                                >
                                    <DeleteLetterDialog.Trigger type={'reset'} asChild={true}>
                                        <DropdownMenu.Item
                                            className={'flex items-center gap-2'}
                                            onSelect={e => e.preventDefault()}
                                        >
                                            <FontAwesomeIcon
                                                className={'text-red-600'}
                                                icon={faTrash}
                                            />
                                            <p className={'text-red-600'}>Delete</p>
                                        </DropdownMenu.Item>
                                    </DeleteLetterDialog.Trigger>
                                </DeleteLetterDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [invalidate, history, match.url, duplicateMessage, letterIndexTab],
    );

    const table = useReactTable({
        data: letters?.data ?? [],
        columns,
        pageCount: letters?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            to:
                letterIndexTab === 'draft'
                    ? cell =>
                          `/accounts/${accountId}/engage/letters/draft/${cell.row.original.id}/edit/details`
                    : cell => `/accounts/${accountId}/engage/letters/${cell.row.original.id}`,
        },
    });

    useEffect(() => {
        const staleOtherTabs = queryClient.getQueryCache().findAll({
            queryKey: accountQueryKeys.engageMessages(accountId),
            stale: true,
            exact: false,
        });

        if (letters) {
            staleOtherTabs.forEach(query => {
                const possibleData = queryClient.getQueryData(query.queryKey);
                if (possibleData) {
                    query.setData({
                        ...possibleData,
                        counts: computeLetterCounts(letters.counts),
                    });
                }
            });
        }
    }, [letters, accountId, queryClient]);

    useSyncPaginatedData(table, letters);

    return (
        <div className={'flex flex-col gap-4'}>
            <SubNav.Root>
                <SubNav.Header>
                    <SubNav.Title>Engage</SubNav.Title>
                    <SubNav.Actions>
                        <NewLetterButton />
                    </SubNav.Actions>
                </SubNav.Header>
                <EngageSubNavTabs />
            </SubNav.Root>
            {!hasDismissedEngageLetterEasySendCallout && (
                <div className="relative flex flex-col rounded-xl border border-gray-200 bg-white bg-[url('/images/illustrations/easy_send_callout_bg.svg')] bg-cover bg-right-bottom bg-no-repeat sm:flex-row">
                    <div className="min-w-[16.5rem] flex-shrink-0 basis-1/4">
                        <img
                            alt="letter mock up"
                            src={'/images/illustrations/easy_send_mock_letter.jpeg'}
                            className="block min-h-full rounded-bl-none rounded-tl-xl rounded-tr-xl object-cover sm:rounded-bl-xl sm:rounded-tr-none"
                        />
                    </div>
                    <div className="flex flex-col gap-4 p-6">
                        <div className="max-w-[33.25rem]">
                            <div className="text-lg font-semibold text-gray-600">
                                Launch direct mail campaigns from start to send 📫
                            </div>
                            <div className="text-sm text-gray-600">
                                Send personalized mailings directly from your Givebutter dashboard
                                to donors' mailboxes. Print, post, send, and track the impact with
                                just a few clicks.
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <Button
                                variant="outline"
                                color="neutral"
                                onClick={() => setHasDismissedEngageLetterEasySendCallout(true)}
                            >
                                Dismiss
                            </Button>

                            <LetterEasySendNewLetterDialog.Root>
                                <LetterEasySendNewLetterDialog.Trigger asChild>
                                    <Button variant="solid">Get started</Button>
                                </LetterEasySendNewLetterDialog.Trigger>
                            </LetterEasySendNewLetterDialog.Root>
                        </div>
                    </div>
                </div>
            )}
            <div className={'flex flex-wrap items-center justify-between gap-4'}>
                <div>
                    <ButtonGroup.Root
                        type="single"
                        value={letterIndexTab}
                        onValueChange={(value: StatusFilter) => setLetterIndexTab(value)}
                    >
                        <ButtonGroup.Item value={'sent'}>
                            <div className={'flex items-center gap-1.5'}>
                                <p>Orders</p>
                                {letters.counts.ordered > 0 && (
                                    <Badge
                                        className={
                                            'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200'
                                        }
                                    >
                                        {letters.counts.ordered}
                                    </Badge>
                                )}
                            </div>
                        </ButtonGroup.Item>
                        <ButtonGroup.Item value={'sending'}>
                            <div className={'flex items-center gap-1.5'}>
                                <p>Printable</p>
                                {letters?.counts.printable! > 0 && (
                                    <Badge
                                        className={
                                            'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200'
                                        }
                                    >
                                        {letters?.counts.printable}
                                    </Badge>
                                )}
                            </div>
                        </ButtonGroup.Item>
                        <ButtonGroup.Item value={'draft'}>
                            <div className={'flex items-center gap-1.5'}>
                                <p>Drafts</p>
                                {letters?.counts.drafts! > 0 && (
                                    <Badge
                                        className={
                                            'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200'
                                        }
                                    >
                                        {letters?.counts.drafts}
                                    </Badge>
                                )}
                            </div>
                        </ButtonGroup.Item>
                    </ButtonGroup.Root>
                </div>
                <Label className="sr-only" htmlFor="letters_search">
                    Search
                </Label>
                <Input.Root size={'sm'}>
                    <Input.SearchIcon />
                    <Input.Form
                        id="letters_search"
                        placeholder={'Search'}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Input.Root>
            </div>
            <Table.Root
                isLoading={isPending}
                variant={'bordered'}
                table={table}
                searchTerm={debouncedSearch}
            >
                <div className={'flex flex-col gap-y-4'}>
                    <Box className={'overflow-x-auto'}>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'primary'}>
                            <Table.EmptyIcon>
                                <FontAwesomeIcon icon={faEnvelope} />
                            </Table.EmptyIcon>
                            <Table.EmptyTitle>No mailings found</Table.EmptyTitle>
                            {allLettersCount === 0 && (
                                <>
                                    <Table.EmptyDescription>
                                        Enhance your communication with supporters by getting
                                        started with your first mailing.
                                    </Table.EmptyDescription>
                                    <Table.EmptyWhenNotSearching>
                                        <NewLetterButton className={'mt-5'} size={'sm'} />
                                    </Table.EmptyWhenNotSearching>
                                </>
                            )}
                        </Table.Empty>
                    </Box>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                        <Table.PaginationPerPageDropdown />
                    </Table.Pagination>
                </div>
            </Table.Root>
        </div>
    );
};

const computeLetterCounts = (counts: EngageMessages['counts']) => ({
    ...counts,
    readyToPrint:
        (counts.preparing ?? 0) +
        (counts.queued ?? 0) +
        (counts.sending ?? 0) +
        (counts.failed ?? 0),
});

interface NewLetterButtonProps extends Omit<ButtonProps, 'variant'> {}
const NewLetterButton = (props: NewLetterButtonProps) => {
    const { className = '', ...rest } = props;

    return (
        <LetterEasySendNewLetterDialog.Root>
            <LetterEasySendNewLetterDialog.LetterEasySendNewLetterDialogTrigger asChild>
                <Button variant="solid" className={className} {...rest}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>New mailing</span>
                </Button>
            </LetterEasySendNewLetterDialog.LetterEasySendNewLetterDialogTrigger>
        </LetterEasySendNewLetterDialog.Root>
    );
};

const WrappedLetters = () => (
    <Content>
        <LettersIndex />
    </Content>
);

export { WrappedLetters as LettersIndex };
